import { useMemo, useCallback } from 'react';
import { useCsvEditorContext } from '../contexts/CsvEditorContext';
import { isRowMissing, isRowChanged } from '../utils/rowHelpers';

/**
 * Hook for row state computation and helpers.
 * Extracts row state logic from useRowFiltering to provide a single source of truth
 * for row state calculations (missing, changed, etc.).
 */
export const useRowState = () => {
  const { rows, originalRowsMap, rowsMap } = useCsvEditorContext();

  // Get row state for a specific row (missing/changed)
  const getRowState = useCallback(
    (rowId) => {
      const row = rowsMap.get(rowId);
      if (!row) {
        return {
          isMissing: false,
          isChanged: false,
          row: null,
          originalRow: null
        };
      }

      const originalRow = originalRowsMap.get(rowId);
      return {
        isMissing: isRowMissing(row),
        isChanged: originalRow ? isRowChanged(row, originalRow) : false,
        row,
        originalRow
      };
    },
    [rowsMap, originalRowsMap]
  );

  // Check if a specific row is missing required fields
  const isRowMissingById = useCallback(
    (rowId) => {
      const row = rowsMap.get(rowId);
      return row ? isRowMissing(row) : false;
    },
    [rowsMap]
  );

  // Check if a specific row has been changed
  const isRowChangedById = useCallback(
    (rowId) => {
      const row = rowsMap.get(rowId);
      if (!row) return false;
      const originalRow = originalRowsMap.get(rowId);
      return originalRow ? isRowChanged(row, originalRow) : false;
    },
    [rowsMap, originalRowsMap]
  );

  // Get all missing rows
  const missingRows = useMemo(() => {
    return rows.filter((row) => isRowMissing(row));
  }, [rows]);

  // Get all changed rows
  const changedRows = useMemo(() => {
    return rows.filter((row) => {
      const originalRow = originalRowsMap.get(row.id);
      return originalRow ? isRowChanged(row, originalRow) : false;
    });
  }, [rows, originalRowsMap]);

  return {
    getRowState,
    isRowMissingById,
    isRowChangedById,
    missingRows,
    changedRows
  };
};
